/**
 * The offshore isles — four reward islands, each opened by an achievement.
 *
 * The only places in this world with a gate that stays shut, so the rules they
 * are held to are worth restating where they are authored:
 *
 * **No mathematics is on an isle, and none can be.** An `Isle` has no
 * `conceptIds`, no lesson, no region and no boss — the type has nowhere to put
 * one, and `isles.test.ts` in `packages/world` asserts the shape stays that
 * way. Every one of the 266 teachable concepts is inside a kingdom on the
 * mainland, reachable by an ordinary road and by an ordinary card, and none of
 * them moved out here. What an isle holds is a drawing and a view.
 *
 * **The key is an achievement, which is a record of work.** Not XP, not level,
 * not gold (§10: nothing unlocks on XP), and not money. Each isle names one
 * entry in `ACHIEVEMENTS`, and each of the four chosen here is earned by doing
 * mathematics rather than by turning up: mastering concepts, keeping a week's
 * practice, answering accurately, coming back to something that beat you.
 *
 * **The lock is stated in words, and the ground is never locked.** Every isle
 * is joined to the mainland by a causeway that a student can always walk, so
 * standing on a shut isle's landing and reading what it is waiting for is
 * always possible. `validateOverworld` proves the causeways with a walk on
 * every `npm run content:validate`.
 *
 * The cosmetics themselves live in `items.ts` beside every other cosmetic, and
 * are conditioned on the same achievement the isle is. That is deliberate: the
 * isle is where a reward is *presented*, and the achievement is what grants it,
 * exactly as a quest page presents work that `recordAttempt` is what credits.
 * One entitlement path, not two.
 */

import type { Isle } from '@yuan-math/world';

export const ISLES: Isle[] = [
  /**
   * First Light — a tidal sandbar off the south-west shore, in sight of
   * Starter Village.
   *
   * Opened by the first concept a student ever masters, so it is the isle
   * almost everybody sees first and the one that teaches what an isle *is*: a
   * small place, a short causeway, a hat, and a view back at the village you
   * came from. Deliberately the cheapest lock on the map — an archipelago whose
   * nearest island costs thirty days is an archipelago nobody believes in.
   */
  {
    id: 'isle-first-light',
    name: 'First Light',
    emoji: '🌅',
    flavor:
      'A sandbar that shows itself at low tide, an hour’s walk from the village. The first place on the map that is only yours because you did something.',
    requiresAchievementId: 'first-mastery',
    rewards: [{ kind: 'item', itemId: 'item-tidewalkers-hat' }],
  },

  /**
   * The Long Watch — a lighthouse rock off the north-west cape.
   *
   * Opened by a seven-day streak, because that is what the place is about: a
   * light nobody has had to relight is a week of turning up, and the reward for
   * it is a keeper's lamp rather than a weapon. Streaks forgive a day (§43), so
   * this is not a punishment for being ill — `streak.current` is read after the
   * rest day has been spent.
   */
  {
    id: 'isle-long-watch',
    name: 'The Long Watch',
    emoji: '🗼',
    flavor:
      'A light on a bare rock, kept burning by whoever last walked out to it. Nobody has had to relight it in a long time.',
    requiresAchievementId: 'streak-7',
    rewards: [
      { kind: 'item', itemId: 'item-keepers-lamp' },
      { kind: 'title', titleId: 'title-lightkeeper' },
    ],
  },

  /**
   * Ledger Bank — a trading flat on the eastern coast, below Linear Algebra.
   *
   * Opened by accuracy rather than by volume: twenty questions at ninety per
   * cent. A bank that opened on sheer number of answers would reward the
   * longest sitting, which is the thing the combo cap already refuses to do.
   */
  {
    id: 'isle-ledger-bank',
    name: 'Ledger Bank',
    emoji: '📒',
    flavor:
      'A sandbank the traders use because the tide leaves it flat and countable. Everything here has been written down twice and checked.',
    requiresAchievementId: 'precision',
    rewards: [{ kind: 'item', itemId: 'item-tally-cloak' }],
  },

  /**
   * The Far Rock — a stack in the northern sea, past the Calculus cape.
   *
   * The farthest walk on the map and the only isle whose reward is the walk:
   * no cosmetic, a title and the view. Opened by `comeback` — mastering a
   * concept you once got wrong three times — which is the achievement in the
   * list that is hardest to get by accident and the one most worth marking. A
   * place whose whole payment is having arrived is a legitimate reward, and
   * `isleRewardIds` supports an empty item list for exactly this.
   */
  {
    id: 'isle-far-rock',
    name: 'The Far Rock',
    emoji: '🪨',
    flavor:
      'The last stack in the northern sea. Nothing on it but weather, and the whole continent laid out behind you.',
    requiresAchievementId: 'comeback',
    rewards: [{ kind: 'title', titleId: 'title-far-walker' }],
  },

  /**
   * North Reach — an island of snow at the top of the world, twenty-five tiles
   * of highway north of the mainland's last shore.
   *
   * The farthest place on the map and the longest walk to anything, and both
   * are the point: the road is the reward before the isle is. It is opened by
   * `twenty-mastered` — twenty concepts mastered, which is mastery proper,
   * earned by practice and never credited by the placement test — because the
   * longest journey should answer to the largest piece of mathematics in the
   * achievement list rather than to the most days served.
   *
   * The parka is the only cosmetic on the platform you cannot get anywhere
   * near the villages, which is the whole of what it says.
   */
  {
    id: 'isle-north-reach',
    name: 'North Reach',
    emoji: '❄️',
    flavor:
      'Snow, a ridge that takes the weather, and two stands of pine in the lee of it. Twenty-five tiles of road behind you and open sea on every other side.',
    requiresAchievementId: 'twenty-mastered',
    rewards: [
      { kind: 'item', itemId: 'item-north-parka' },
      { kind: 'title', titleId: 'title-northwalker' },
    ],
  },

  /**
   * The Sunken Reach — an erg of dunes thirty tiles south of the last shore.
   *
   * The north road's answer, and deliberately its pair: the same argument about
   * distance made at the other end of the continent, so the middle of the map
   * reads as the middle of something. Where the northern trial goes deep — the
   * hardest rung you have reached, climbing — this one goes wide, one concept
   * per strand, asking how much of the map you carry rather than how far up it
   * you have got.
   *
   * Opened by `wide-reader`, which is the achievement for practising ten
   * different concepts, because the key should be the same shape as the lock:
   * a breadth trial answers to breadth.
   */
  {
    id: 'isle-south-reach',
    name: 'The Sunken Reach',
    emoji: '🏜️',
    flavor:
      'Dunes as far as the heat lets you see, one rock the wind has never moved, and an oasis in the lee of it. Thirty tiles of road behind you and nothing ahead.',
    requiresAchievementId: 'wide-reader',
    rewards: [
      { kind: 'item', itemId: 'item-sun-shawl' },
      { kind: 'title', titleId: 'title-duneswalker' },
    ],
  },
];
